import type { Violation } from './types';

/**
 * Une réponse d'erreur de l'API (RFC 7807, `application/problem+json`).
 *
 * Les écrans n'inspectent jamais une `Response` : ils reçoivent ceci, avec le
 * statut, le `type` — sur lequel on reconnaît une situation, jamais sur le texte
 * — et les violations de validation rattachées à leur champ.
 */
export class ApiProblem extends Error {
    constructor(
        readonly status: number,
        readonly type: string,
        readonly detail: string,
        readonly violations: readonly Violation[],
        /** Les membres d'extension du problème (RFC 7807 §3.2), ex. `attemptsLeft`. */
        readonly extra: Readonly<Record<string, unknown>> = {},
    ) {
        super(detail === '' ? `HTTP ${status}` : detail);
        this.name = 'ApiProblem';
    }

    /** La première violation rattachée à ce champ, s'il y en a une. */
    violationFor(path: string): Violation | null {
        return this.violations.find((violation) => violation.propertyPath === path) ?? null;
    }

    static async from(response: Response): Promise<ApiProblem> {
        let body: unknown = null;

        try {
            body = await response.json();
        } catch {
            // Corps vide ou non JSON : le statut suffit.
        }

        const record = typeof body === 'object' && body !== null ? (body as Record<string, unknown>) : {};
        const violations = Array.isArray(record.violations)
            ? record.violations.flatMap((raw: unknown): Violation[] => {
                  if (typeof raw !== 'object' || raw === null) {
                      return [];
                  }

                  const item = raw as Record<string, unknown>;

                  return [
                      {
                          propertyPath: typeof item.propertyPath === 'string' ? item.propertyPath : '',
                          message: typeof item.message === 'string' ? item.message : '',
                          code: typeof item.code === 'string' ? item.code : null,
                      },
                  ];
              })
            : [];

        return new ApiProblem(
            response.status,
            typeof record.type === 'string' ? record.type : 'about:blank',
            typeof record.detail === 'string' ? record.detail : '',
            violations,
            record,
        );
    }

    /** Un membre d'extension numérique, ou null. */
    number(name: string): number | null {
        const value = this.extra[name];

        return typeof value === 'number' ? value : null;
    }
}

/** Le `type` du 403 que pose TwoFactorEnrollmentGate (D39). */
export const TWO_FACTOR_SETUP_REQUIRED = '/problems/two-factor-setup-required';

/** Le `type` du 401 d'un défi de second facteur mort (D39) : retour au mot de passe. */
export const TWO_FACTOR_EXPIRED = '/problems/two-factor-expired';

/** Le `type` du 403 que pose PasswordChangeGate (D32). */
export const PASSWORD_CHANGE_REQUIRED = '/problems/password-change-required';
